import { useState, useEffect, useRef } from 'react';
import { Search, X, Plus, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { getCurrentUserOrganizationId } from '../../lib/stores/referenceDataCache';
import { createFirstClassEntity } from '../../lib/utils/createContactEntity';
import type { Client } from '../../lib/types';

interface Props {
  /** entity_type dei candidati da cercare ('company' o 'individual') */
  targetEntityType: 'company' | 'individual';
  /** id già collegati (o se stesso) da escludere */
  excludeIds: string[];
  onPick: (client: Pick<Client, 'id' | 'business_name'>) => void;
  onClose: () => void;
}

/**
 * Ricerca compatta di un client (azienda o persona) da collegare.
 * Usato da ContactCompanyLinksSection per creare un nuovo link 'contatto'.
 */
export function ContactCompanyLinkPicker({ targetEntityType, excludeIds, onPick, onClose }: Props) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Array<Pick<Client, 'id' | 'business_name'>>>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const trimmed = term.trim();
  const hasExact = results.some((c) => (c.business_name || '').trim().toLowerCase() === trimmed.toLowerCase());
  const canCreate = trimmed.length >= 2 && !hasExact && !loading;

  const handleCreate = async () => {
    if (!trimmed || creating) return;
    setCreating(true);
    const orgId = await getCurrentUserOrganizationId();
    const created = orgId ? await createFirstClassEntity(orgId, trimmed, targetEntityType) : null;
    setCreating(false);
    if (created) onPick(created);
  };

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults([]); return; }
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      const { data: userData } = await supabase.auth.getUser();
      const { data: profile } = await supabase
        .from('user_profiles').select('organization_id').eq('id', userData.user?.id).single();
      const { data } = await supabase
        .from('clients')
        .select('id, business_name')
        .eq('organization_id', profile?.organization_id)
        .eq('entity_type', targetEntityType)
        .ilike('business_name', `%${q}%`)
        .limit(8);
      if (!active) return;
      setResults((data || []).filter((c) => !excludeIds.includes(c.id)));
      setLoading(false);
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [term, targetEntityType, excludeIds]);

  return (
    <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-gray-400" />
        <input
          ref={inputRef}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (results.length === 1) onPick(results[0]);
              else if (canCreate) handleCreate();
            }
          }}
          placeholder={targetEntityType === 'company' ? 'Cerca o crea azienda…' : 'Cerca o crea persona…'}
          className="flex-1 bg-transparent text-sm outline-none"
        />
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Chiudi">
          <X className="w-4 h-4" />
        </button>
      </div>
      {trimmed.length >= 2 && (
        <div className="mt-2 max-h-48 overflow-auto">
          {loading && <div className="text-xs text-gray-400 px-1 py-2">Ricerca…</div>}
          {results.map((c) => (
            <button
              key={c.id}
              onClick={() => onPick(c)}
              className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-white"
            >
              {c.business_name}
            </button>
          ))}
          {/* Crea-e-collega (stile Notion): appare se non c'è già un match esatto */}
          {canCreate && (
            <button
              onClick={handleCreate}
              disabled={creating}
              className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-white flex items-center gap-1.5 text-[#2383E2] disabled:opacity-60"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" /> : <Plus className="w-3.5 h-3.5 flex-shrink-0" />}
              <span className="truncate">Crea {targetEntityType === 'company' ? 'azienda' : 'persona'} «{trimmed}»</span>
            </button>
          )}
          {!loading && results.length === 0 && !canCreate && (
            <div className="text-xs text-gray-400 px-1 py-2">Digita un nome per cercare o creare</div>
          )}
        </div>
      )}
    </div>
  );
}
